import { supabase } from '@/lib/supabase';
import type { Category, CategoryWithRelations } from '@/features/categories/types';

export const categoryQueries = {
  getCategories: async (providerId: number, versionId: number): Promise<CategoryWithRelations[]> => {
    const { data, error } = await supabase
      .from('categories')
      .select(`
        *,
        children:categories!parent_id(
          id,
          name,
          parent_id,
          price,
          description,
          display_order,
          channels:channels(count)
        ),
        channels:channels(count)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .order('display_order');

    if (error) throw error;

    return data.map(category => ({
      ...category,
      channels_count: category.channels?.[0]?.count || 0,
      children: (category.children as any)?.map((child: any) => ({
        ...child,
        channels_count: child.channels?.[0]?.count || 0
      })).sort((a: any, b: any) => a.display_order - b.display_order) || []
    })) as unknown as CategoryWithRelations[];
  },

  getCategoriesHierarchical: async (providerId: number, versionId: number): Promise<CategoryWithRelations[]> => {
    const { data, error } = await supabase
      .from('categories')
      .select(`
        *,
        children:categories!parent_id(
          id,
          name,
          parent_id,
          price,
          description,
          display_order,
          channels:channels(count)
        ),
        channels:channels(count)
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .order('display_order');

    if (error) throw error;

    const categoriesWithCounts = data.map(category => ({
      ...category,
      channels_count: category.channels?.[0]?.count || 0,
      children: (category.children as any)?.map((child: any) => ({
        ...child,
        channels_count: child.channels?.[0]?.count || 0
      })).sort((a: any, b: any) => a.display_order - b.display_order) || []
    })) as CategoryWithRelations[];

    // Separate parent categories and build hierarchical structure
    const parentCategories = categoriesWithCounts.filter(cat => !cat.parent_id);
    const hierarchicalCategories: CategoryWithRelations[] = [];

    // Build the flat hierarchical list: parent -> children -> parent -> children
    parentCategories.forEach(parent => {
      hierarchicalCategories.push(parent as CategoryWithRelations);
      if (parent.children && parent.children.length > 0) {
        // Add parent name to each child category for breadcrumb display
        const childrenWithParentName = parent.children.map((child: CategoryWithRelations) => ({
          ...child,
          parent_name: parent.name
        }));
        hierarchicalCategories.push(...childrenWithParentName);
      }
    });

    return hierarchicalCategories;
  },

  getCategoryById: async (categoryId: number) => {
    const { data, error } = await supabase
      .from('categories')
      .select(`
        *,
        channels:channels(count)
      `)
      .eq('id', categoryId)
      .single();

    if (error) throw error;
    return {
      ...data,
      channels_count: data.channels?.[0]?.count || 0
    } as Category & { channels_count: number };
  }
};
